/**
 * Socket.IO context for real-time updates
 * Manages WebSocket connection lifecycle and provides event subscription hooks
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { io } from "socket.io-client";
import { SurveyContext } from "./SurveyContext";
import { AuthContext } from "./AuthContext";

export const SocketContext = createContext();

// Socket server URL - defaults to same origin in production
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL ||
  (process.env.REACT_APP_API_URL || "http://localhost:5001").replace("/api", "");

export const SocketProvider = ({ children }) => {
  const { instanceId } = useContext(SurveyContext);
  const { token } = useContext(AuthContext);

  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);

  // Event listeners registry
  const listenersRef = useRef({});

  // Initialize socket connection when instanceId is available
  useEffect(() => {
    if (!instanceId) {
      // Clean up existing connection if instanceId becomes null
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Create new socket connection
    const newSocket = io(SOCKET_URL, {
      auth: {
        token: token || null,
        instanceId,
      },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    // Connection event handlers
    newSocket.on("connect", () => {
      console.log("[Socket] Connected to server");
      setIsConnected(true);
      setConnectionError(null);
    });

    newSocket.on("disconnect", (reason) => {
      console.log("[Socket] Disconnected:", reason);
      setIsConnected(false);
    });

    newSocket.on("connect_error", (error) => {
      console.error("[Socket] Connection error:", error.message);
      setConnectionError(error.message);
      setIsConnected(false);
    });

    newSocket.on("error", (error) => {
      console.error("[Socket] Error:", error);
      setConnectionError(error.message || "Unknown error");
    });

    setSocket(newSocket);

    // Cleanup on unmount or instanceId change
    return () => {
      console.log("[Socket] Cleaning up connection");
      newSocket.disconnect();
    };
  }, [instanceId]); // Don't include token to avoid reconnects on token changes

  // Visibility change detection — pause orchestrator when tab is hidden
  useEffect(() => {
    if (!socket || !isConnected) return;

    let hideTimer = null;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Debounce: wait 5 minutes before signaling pause.
        // Short debounces (10s) cause frequent pause/resume cycles that
        // interrupt agent behavior. During a 20-min study session,
        // participants may briefly tab away — this is normal, not abandonment.
        hideTimer = setTimeout(() => {
          console.log("[Socket] Tab hidden for 5min, signaling pause");
          socket.emit("user:visibility", { visible: false });
        }, 300000);
      } else {
        // Cancel pending pause if user returns quickly
        if (hideTimer) {
          clearTimeout(hideTimer);
          hideTimer = null;
        }
        // Immediately signal unpause
        console.log("[Socket] Tab visible, signaling unpause");
        socket.emit("user:visibility", { visible: true });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [socket, isConnected]);

  // Disconnect socket explicitly (called before navigation on session end)
  const disconnectSocket = useCallback(() => {
    if (socket) {
      console.log("[Socket] Disconnecting socket (session ended)");
      socket.disconnect();
    }
  }, [socket]);

  // Track if this is a fresh login vs page reload with stored token
  const [hasActivelyAuthenticated, setHasActivelyAuthenticated] = useState(false);

  // Function to manually trigger auth update (call this after login/register)
  // Accepts optional tokenOverride for cases where React state hasn't updated yet
  const authenticateSocket = useCallback((tokenOverride = null) => {
    const authToken = tokenOverride || token;
    if (socket && authToken) {
      console.log("[Socket] Authenticating socket as human user");
      socket.emit("auth:update", { token: authToken });
      setHasActivelyAuthenticated(true);
    } else {
      console.warn("[Socket] Cannot authenticate - socket or token not available");
    }
  }, [socket, token]);

  // Don't auto-authenticate on page load - wait for explicit login action
  // The auth:update should only be sent when user actively logs in, not on page refresh

  // Subscribe to an event
  const subscribe = useCallback((event, callback) => {
    if (!socket) return () => {};

    // Add to registry for tracking
    if (!listenersRef.current[event]) {
      listenersRef.current[event] = [];
    }
    listenersRef.current[event].push(callback);

    // Register with socket
    socket.on(event, callback);

    // Return unsubscribe function
    return () => {
      socket.off(event, callback);
      const idx = listenersRef.current[event]?.indexOf(callback);
      if (idx > -1) {
        listenersRef.current[event].splice(idx, 1);
      }
    };
  }, [socket]);

  // Emit an event
  const emit = useCallback((event, data) => {
    if (!socket || !isConnected) {
      console.warn("[Socket] Cannot emit - not connected");
      return false;
    }
    socket.emit(event, data);
    return true;
  }, [socket, isConnected]);

  // Join a different instance room (for loading screen before auth)
  const joinInstance = useCallback((newInstanceId) => {
    if (socket && newInstanceId) {
      socket.emit("join:instance", newInstanceId);
    }
  }, [socket]);

  const value = {
    socket,
    isConnected,
    connectionError,
    subscribe,
    emit,
    joinInstance,
    authenticateSocket, // Call this after login/register to mark user as human
    disconnectSocket, // Call before navigating away on session end
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

/**
 * Hook to subscribe to socket events with automatic cleanup
 * @param {string} event - Event name
 * @param {function} callback - Event handler
 * @param {array} deps - Dependencies for callback
 */
export const useSocketEvent = (event, callback, deps = []) => {
  const { subscribe } = useContext(SocketContext);

  useEffect(() => {
    if (!event || !callback) return;

    const unsubscribe = subscribe(event, callback);
    return unsubscribe;
  }, [event, subscribe, ...deps]);
};

/**
 * Hook to get socket connection status
 */
export const useSocketStatus = () => {
  const { isConnected, connectionError } = useContext(SocketContext);
  return { isConnected, connectionError };
};

/**
 * Hook for real-time newsfeed updates
 */
export const useNewsfeedSocket = (onNewMessage, onMessageUpdate) => {
  const { subscribe } = useContext(SocketContext);

  useEffect(() => {
    if (!subscribe) return;

    const unsubscribers = [];

    if (onNewMessage) {
      unsubscribers.push(subscribe("message:new", (data) => {
        onNewMessage(data.message);
      }));
    }

    if (onMessageUpdate) {
      unsubscribers.push(subscribe("message:update", (data) => {
        onMessageUpdate(data.messageId, data.updateType, data.data);
      }));
    }

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [subscribe, onNewMessage, onMessageUpdate]);
};

/**
 * Hook for real-time notification updates
 */
export const useNotificationSocket = (onNewNotification) => {
  const { subscribe } = useContext(SocketContext);

  useEffect(() => {
    if (!subscribe || !onNewNotification) return;

    return subscribe("notification:new", (data) => {
      onNewNotification(data.notification);
    });
  }, [subscribe, onNewNotification]);
};

/**
 * Hook for instance status updates (loading screen)
 */
export const useInstanceStatusSocket = (onStatusUpdate) => {
  const { subscribe } = useContext(SocketContext);

  useEffect(() => {
    if (!subscribe || !onStatusUpdate) return;

    return subscribe("instance:status", (data) => {
      onStatusUpdate(data.status, data.progress, data.statusMessage);
    });
  }, [subscribe, onStatusUpdate]);
};

/**
 * Hook for real-time comment updates
 */
export const useCommentSocket = (onNewComment) => {
  const { subscribe } = useContext(SocketContext);

  useEffect(() => {
    if (!subscribe || !onNewComment) return;

    return subscribe("comment:new", (data) => {
      onNewComment(data.comment);
    });
  }, [subscribe, onNewComment]);
};

/**
 * Hook for agent activity updates (typing indicators, etc.)
 */
export const useAgentActivitySocket = (onAgentActivity) => {
  const { subscribe } = useContext(SocketContext);

  useEffect(() => {
    if (!subscribe || !onAgentActivity) return;

    return subscribe("agent:activity", (data) => {
      onAgentActivity(data);
    });
  }, [subscribe, onAgentActivity]);
};
